import {
  combineSlices,
  configureStore,
  createDynamicMiddleware,
} from "@reduxjs/toolkit";
import { snackbarSlice } from "@/features/alertSnackbarSlice";
import type { GenericObject } from "@/types/common";
import { externalEventSyncSlice } from "./features/currentRouteSlice";
import { globalStateSlice } from "./features/globalStateSlice/globalStateSlice";
import { userSlice } from "./features/userSlice";
import type { LazyLoadedSlices } from "./store.constants";
const preloadedState = {};

// Create root reducer with lazy loading support
export const rootReducer = combineSlices({
  [globalStateSlice.name]: globalStateSlice.reducer, // global state
  [snackbarSlice.name]: snackbarSlice.reducer, // notifications
  [userSlice.name]: userSlice.reducer, // user
  [externalEventSyncSlice.name]: externalEventSyncSlice.reducer, // breadcrumbs
}).withLazyLoadedSlices<LazyLoadedSlices>();

export const injectSlice = <K extends keyof LazyLoadedSlices>(slice: {
  reducerPath: K;
  reducer: LazyLoadedSlices[K];
}) => {
  // @ts-expect-error -  Type mismatch between LazyLoadedSlices and Redux Toolkit's combineSlices inject method
  rootReducer.inject(slice);
};

export type RootState = ReturnType<typeof rootReducer>;

// create dynamic middleware middleware for each api
export const dynamicMiddleware = createDynamicMiddleware();

/**
 * Factory function to create Redux stores.
 * Used both for the main app store and for creating isolated test stores.
 */
export const setupStore = (preloadedState: GenericObject<unknown>) =>
  configureStore({
    reducer: rootReducer,
    devTools: process.env.NODE_ENV !== "production",
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: { warnAfter: 128 },
      }).concat([dynamicMiddleware.middleware]), // add here non dynamic middleware
    preloadedState,
  });

export const store = setupStore(preloadedState);

export type AppDispatch = typeof store.dispatch;
